import {
  CartLocalStorage,
  EnquiryPageName,
  FilterPageName,
  FiltersLocalStorage,
  OccupancyLocalStorage,
  RequestedCidsLocalStorage,
  ValidLanguages,
  VersionLocalStorage
} from '@/constants'
import t from '../i18n/translations'
import type { IAccoItem } from './Interfaces/apiInterfaces'
import { broadcasterFilterStore, filtersData, filterStore, initFilterStoreFromLocalStorage, messageReceiverFilterStore } from '@/store/filter.store'
import type { IEnquiryFilter, IFiltersDataInterface, IPackageDto, IRoomDto, IRoomTypeDto } from './Interfaces/appInterfaces'
import { version } from '../../package.json'
import { EnquiryType } from '@/store/enquiry.store'
import { useFilterApi } from './useApi'
import { broadcasterAccosBarStore, date, initAccosBarStoreFromLocalStorage, messageReceiverAccosBarStore } from '@/store/accosbar.store'
import { broadcasterCartStore, initCartStoreFromLocalStorage, messageReceiverCartStore } from '@/store/cart.store'

export let PID: number
export let CULTURE: string
export let AREAID: number | null
export let MAP_ID: string | undefined
export let MAP_KEY: string | undefined
export let CID: number | undefined // is only used in enquirypage for landingpage

export function setGlobalVars(pid: number, l: string, areaid: number, mapKey: string, mapId: string, cid: number): void {
  PID = pid
  CULTURE = l
  if (areaid) AREAID = areaid
  if (mapKey) MAP_KEY = mapKey
  if (mapId) MAP_ID = mapId
  if (cid) CID = cid
}

export function toFormattedStringDateShort(): string | null {
  if (date.value != null && date.value[0] && date.value[1] != null) {
    const startDate = date.value[0]
    const endDate = date.value[1]
    return `${formatDateShort(startDate)} - ${formatDateShort(endDate)}`
  }
  return null
}

function formatDateShort(date: Date): string {
  const dayOfWeek = date.toLocaleString(CULTURE, { weekday: 'short' })
  const day = date.toLocaleString(CULTURE, { day: '2-digit' })
  const month = date.toLocaleString(CULTURE, { month: 'short' })

  return `${dayOfWeek}.., ${day} ${month}.`
}

export function toFormattedStringDateLong(): string | null {
  if (date.value != null && date.value[0] && date.value[1] != null) {
    const startDate = date.value[0]
    const endDate = date.value[1]
    return `${formatDateLong(startDate)} - ${formatDateLong(endDate)}`
  }
  return null
}

function formatDateLong(date: Date): string {
  const dayOfWeek = date.toLocaleString(CULTURE, { weekday: 'short' })
  const day = date.toLocaleString(CULTURE, { day: '2-digit' })
  const month = date.toLocaleString(CULTURE, { month: 'long' })
  const year = date.toLocaleString(CULTURE, { year: 'numeric' })

  return `${dayOfWeek}, ${day}. ${month} ${year}`
}

export function toFormattedRoomObj(rooms: any) {
  if (!rooms) {
    return null
  }
  return rooms.reduce((accumulator: any, currentValue: any) => {
    accumulator = {
      adults: accumulator.adults + currentValue.adults,
      children: accumulator.children + currentValue.children
    }
    return accumulator
  })
}

export function toFormattedRoomStringShort(rooms: any, l: string): string | null {
  const summedRoomValues = toFormattedRoomObj(rooms)

  let result = `${summedRoomValues.adults} ${t(`${l}.occupancy.adults`)}`

  if (summedRoomValues.children > 0) {
    const childrenTranslation = summedRoomValues.children === 1 ? t(`${l}.occupancy.child`) : t(`${l}.occupancy.children`)

    result += `, ${summedRoomValues.children} ${childrenTranslation}`
  }

  return result
}

export function toFormattedRoomStringLong(rooms: any, l: string): string | null {
  const summedRoomValues = toFormattedRoomObj(rooms)

  let chilrenTranslation = ''
  switch (summedRoomValues.children) {
    case 1:
      chilrenTranslation = t(`${l}.occupancy.child`)
      break
    default:
      chilrenTranslation = t(`${l}.occupancy.children`)
      break
  }

  return `${summedRoomValues.adults} ${t(`${l}.occupancy.adults`)} - ${summedRoomValues.children} ${chilrenTranslation} - ${rooms.length} ${t(`${l}.occupancy.rooms`)}`
}

export function mediaBreakpointsAsNumber(mediaBreakpoint: string) {
  const number = Number(mediaBreakpoint.replace('px', ''))
  return number
}

export function setLocale(l: string) {
  let locale = {}
  switch (l) {
    case 'de':
      locale = {
        emptySearchMessage: 'Keine Ergebnisse gefunden',
        firstDayOfWeek: 1,
        dayNames: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
        dayNamesShort: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
        dayNamesMin: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
        monthNames: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
        monthNamesShort: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']
      }
      break
    case 'it':
      locale = {
        emptySearchMessage: 'Nessun risultato trovato',
        firstDayOfWeek: 1,
        dayNames: ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'],
        dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'],
        dayNamesMin: ['Do', 'Lu', 'Ma', 'Me', 'Gi', 'Ve', 'Sa'],
        monthNames: ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'],
        monthNamesShort: ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic']
      }
      break
    case 'en':
      locale = {
        emptySearchMessage: 'No results found',
        firstDayOfWeek: 1,
        dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
        monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
        monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      }
      break
    case 'fr':
      locale = {
        emptySearchMessage: 'Aucun résultat trouvé',
        firstDayOfWeek: 1,
        dayNames: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
        dayNamesShort: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
        dayNamesMin: ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'],
        monthNames: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
        monthNamesShort: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc']
      }
      break
    case 'nl':
      locale = {
        emptySearchMessage: 'Nie znaleziono wyników',
        firstDayOfWeek: 1,
        dayNames: ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'],
        dayNamesShort: ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'],
        dayNamesMin: ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'],
        monthNames: ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'],
        monthNamesShort: ['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec']
      }
      break
    case 'pl':
      locale = {
        emptySearchMessage: 'Geen resultaten gevonden',
        firstDayOfWeek: 1,
        dayNames: ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota'],
        dayNamesShort: ['Nie', 'Pon', 'Wto', 'Śro', 'Czw', 'Pią', 'Sob'],
        dayNamesMin: ['Ni', 'Po', 'Wt', 'Śr', 'Cz', 'Pi', 'So'],
        monthNames: ['Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec', 'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'],
        monthNamesShort: ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru']
      }
      break
    default:
      break
  }
  return locale
}

export function handleInvalidLanguage(l: string) {
  const bool = ValidLanguages.includes(l)
  if (bool == false) throw new Error(`not supported language: ${l}`)
}

export function uspsOrShortDescription(item: IAccoItem): boolean {
  return item.usps[0].length === 0 ? true : false
}

export function setLocationToEnquiryPage(enquiryType: EnquiryType, cid: number | null): void {
  const env = process.env.NODE_ENV
  let url = ''
  if (enquiryType === EnquiryType.Direct) {
    env === 'development' ? (url = `/enquirypage#cid=${cid!}`) : (url = `/enquiry#cid=${cid!}`)
  } else if (enquiryType === EnquiryType.Collective) {
    env === 'development' ? (url = '/enquirypage') : (url = '/enquiry')
  } else {
    env === 'development' ? (url = '/enquirypagegeneral') : (url = '/enquirygeneral')
  }
  window.location.href = url
}

export function getFilterDto(cids: number[]): IEnquiryFilter {
  //excludedCompanies is used to filter out companies for similaraccos on enquiry success page
  if (filterStore.value != null) {
    return {
      areaIds: filterStore.value.areaId !== 0 && filterStore.value.areaId != null ? [filterStore.value.areaId] : [],
      regionIds: null!,
      themeIds: filterStore.value.themeId !== 0 && filterStore.value.themeId != null ? [filterStore.value.themeId] : [],
      accotypes: filterStore.value.accotypeIds.map((id) => ({
        id,
        ratingFrom: filterStore.value.ratingFrom,
        ratingTo: filterStore.value.ratingTo
      })),
      amenityIds: filterStore.value.amenityIds,
      boardingIds: filterStore.value.boardingIds,
      excludeCompanyIds: getExcludedCompaniesAndSetLocalStorage(cids)
    }
  } else {
    return {
      areaIds: undefined,
      regionIds: undefined,
      themeIds: undefined,
      accotypes: undefined,
      amenityIds: undefined,
      boardingIds: undefined,
      excludeCompanyIds: getExcludedCompaniesAndSetLocalStorage(cids)
    }
  }
}

export function getExcludedCompaniesAndSetLocalStorage(cids: number[]): number[] {
  //this is used, to be able to filter out already requested cids for the similar accos on the enquiry success page
  var localStorageData = localStorage.getItem(RequestedCidsLocalStorage)
  if (localStorageData != null) {
    const storedData: number[] = JSON.parse(localStorageData)
    cids.forEach((cid) => {
      if (!storedData.includes(cid)) storedData.push(cid)
    })
    localStorage.setItem(RequestedCidsLocalStorage, JSON.stringify(storedData))
    return storedData
  } else {
    localStorage.setItem(RequestedCidsLocalStorage, JSON.stringify(cids))
    return cids
  }
}

export function setVersionInLocalStorage(): void {
  localStorage.setItem(VersionLocalStorage, version)
}

export function delteLocalStorageIfOldVersion(): void {
  var localStorageVersion = localStorage.getItem(VersionLocalStorage)

  if (localStorageVersion != version) {
    console.log('deleting pui localstorage')
    localStorage.removeItem(OccupancyLocalStorage)
    localStorage.removeItem(CartLocalStorage)
    localStorage.removeItem(FiltersLocalStorage)
  }
}

export function roomName(room: IRoomDto | undefined, culture: string) {
  if (room)
    switch (culture) {
      case 'de':
        return room.nameDe
      case 'it':
        return room.nameIt
      case 'en':
        return room.nameEn
      default:
        return room.nameEn
    }
}

export function roomTypeName(roomType: IRoomTypeDto | undefined, culture: string) {
  if (roomType)
    switch (culture) {
      case 'de':
        return roomType.nameDe
      case 'it':
        return roomType.nameIt
      case 'en':
        return roomType.nameEn
      default:
        return roomType.nameEn
    }
}

export function packageName(room: IPackageDto | undefined, culture: string) {
  if (room)
    switch (culture) {
      case 'de':
        return room.titleDe
      case 'it':
        return room.titleIt
      case 'en':
        return room.titleEn
      default:
        return room.titleEn
    }
}

export async function ensureStoresAreFilled(componentType: string): Promise<void> {
  initFilterStoreFromLocalStorage()
  initAccosBarStoreFromLocalStorage()
  await initCartStoreFromLocalStorage()
  if (componentType == FilterPageName || componentType == EnquiryPageName) {
    filtersData.value = (await useFilterApi()).data
    if (filtersData.value != null) parseBoardings(filtersData.value)
  }
}

function parseBoardings(filtersDataBoarding: IFiltersDataInterface): void {
  //we get from the endpoint int[] BoardTypeIds which we convert to:
  filtersDataBoarding.boardings = filtersDataBoarding.boardings.map((item) => {
    return {
      ...item,
      ids: Array.isArray(item.ids) ? item.ids.join(',') : item.ids
    }
  })
}

export const broadcastChannelMessages = {
  UPDATE: 'update',
  ACCOSBARUPDATE: 'accosbarupdate'
}

export function garbageCollectBroadcastChannels(): void {
  broadcasterFilterStore.close()
  messageReceiverFilterStore.close()

  broadcasterCartStore.close()
  messageReceiverCartStore.close()

  broadcasterAccosBarStore.close()
  messageReceiverAccosBarStore.close()
}
